import * as XLSX from 'xlsx';
import { formatInTimeZone } from 'date-fns-tz';
import { FLAG_LABEL } from '../flags';
import { slugify } from './exportClosure';
import type { ValidationRow } from '../../api/attendance';
import type { TurnoMarca, TurnoRow } from '../../api/positions';
import type { CanjeRow } from '../../api/canjes';
import { banderasDeCanje, registrosVigentes, totalesGenerales } from '../canjesTotales';

const TZ = 'America/Lima';

/**
 * Perú no tiene horario de verano: el offset es -05:00 los 365 días del año.
 *
 * Se usa para armar el instante de inicio/fin de un turno a partir de su fecha y su hora. Con la
 * zona por nombre habría que arrastrar una librería de husos hasta acá; con el offset fijo,
 * `new Date('2026-08-15T18:00:00-05:00')` ya es el instante exacto y NO depende de la zona del
 * equipo que abre la app —un navegador en UTC daría el mismo turno por terminado cinco horas antes.
 */
const LIMA_OFFSET = '-05:00';

/**
 * Cuánto vive un enlace de foto puesto en un reporte descargable: **un año**.
 *
 * Vive acá, en el módulo puro, y `api/attendance.ts` lo importa: el número que se usa al firmar
 * y el que se escribe en la hoja `Resumen` tienen que ser el mismo, o el archivo mentiría sobre
 * su propia fecha de vencimiento. Cambiarlo es cambiar esta línea.
 */
export const ENLACE_REPORTE_SEGUNDOS = 365 * 24 * 60 * 60;

/**
 * Metadatos de cabecera comunes a los dos reportes. Van en la hoja `Resumen` para que el
 * archivo se explique solo: un Excel suelto en el correo de alguien, sin quién lo pidió ni
 * de qué rango es, no sirve de evidencia de nada.
 */
export interface ReporteMeta {
  campaignName: string;
  periodStart: string;
  periodEnd: string;
  /** Filtros activos al descargar, ya en texto. El reporte tiene que decir qué NO trae. */
  filtros: string[];
}

/**
 * Enlaces de foto ya resueltos, por ruta del Storage. Los arma `firmarFotosParaReporte`.
 *
 * Son URL firmadas de vencimiento largo, que **abren sin iniciar sesión**: decisión explícita
 * del jefe de operaciones (2026-08-18), porque el reporte tiene que poder mandarse a alguien
 * que no tiene usuario del sistema. El token va dentro de la URL, así que quien reciba el
 * archivo ve esas fotos.
 *
 * El módulo no firma nada por su cuenta a propósito: así queda puro y comprobable, y la
 * decisión de qué enlace se pega vive en un solo sitio (`api/attendance.ts`).
 */
export type EnlacesDeFoto = Map<string, string>;

/** El enlace de una foto, o cadena vacía si no hay foto o no se pudo firmar. */
export function photoLink(enlaces: EnlacesDeFoto, path: string | null): string {
  if (path === null || path.trim() === '') return '';
  return enlaces.get(path) ?? '';
}

/** Celda de texto que además es hipervínculo. Sin `l` Excel la pinta como texto plano. */
function celdaEnlace(texto: string, url: string): XLSX.CellObject {
  return url === ''
    ? { t: 's', v: '' }
    : { t: 's', v: texto, l: { Target: url, Tooltip: 'Abre la foto' } };
}

/**
 * Cuándo dejan de abrir los enlaces de foto de este archivo.
 *
 * Va escrito DENTRO del reporte porque nadie va a recordarlo. Un Excel con enlaces que un día
 * dejan de funcionar, sin decir cuándo ni por qué, se lee como que el sistema se rompió.
 */
function lineaVencimiento(generatedAt: Date, vigenciaSegundos: number): string {
  const vence = new Date(generatedAt.getTime() + vigenciaSegundos * 1000);
  return `Abren sin iniciar sesión · dejan de funcionar el ${formatInTimeZone(vence, TZ, 'dd/MM/yyyy')}`;
}

/**
 * `alcance` es opcional porque solo el reporte de asistencia lo necesita: es el único con dos
 * hojas que NO cubren lo mismo, y esa diferencia tiene que estar escrita en el archivo.
 */
function hojaResumen(meta: ReporteMeta, titulo: string, conteos: [string, number][],
                     generatedAt: Date, vigenciaSegundos: number,
                     alcance?: string): XLSX.WorkSheet {
  const ws = XLSX.utils.aoa_to_sheet([
    [titulo],
    ['Campaña', meta.campaignName],
    ['Periodo', `${meta.periodStart} a ${meta.periodEnd}`],
    ['Filtros', meta.filtros.length === 0 ? 'Sin filtros: todo el rango' : meta.filtros.join(' · ')],
    ...(alcance === undefined ? [] : [['Alcance', alcance]]),
    ['Generado el', formatInTimeZone(generatedAt, TZ, 'dd/MM/yyyy HH:mm')],
    ['Enlaces de foto', lineaVencimiento(generatedAt, vigenciaSegundos)],
    ['Reservado', 'Los enlaces abren la foto sin pedir usuario: trata este archivo como '
      + 'información reservada al reenviarlo.'],
    [],
    ...conteos.map(([k, v]) => [k, v] as [string, number]),
  ]);
  ws['!cols'] = [{ wch: 26 }, { wch: 78 }];
  return ws;
}

// ---------------------------------------------------------------------------
// Asistencia
// ---------------------------------------------------------------------------

export const TURNOS_HEADERS = [
  'Fecha', 'Tienda', 'Recurso', 'Documento', 'Turno', 'Ingreso', 'Salida', 'Horas',
  'Cumplimiento', 'Alertas', 'Estado', 'Nota', 'GPS ingreso', 'GPS salida',
  'Foto ingreso', 'Foto salida',
];

export const ASISTENCIA_HEADERS = [
  'Fecha', 'Tienda', 'Recurso', 'Documento', 'Tipo', 'Hora', 'Turno',
  'Alertas', 'Estado', 'Nota', 'GPS', 'Foto',
];

const ESTADO_ASISTENCIA: Record<string, string> = {
  pendiente: 'Pendiente', validada: 'Validada', observada: 'Observada',
};

/**
 * Las dos hojas de asistencia NO cubren lo mismo, y el archivo lo tiene que decir en su primera
 * página. Un botón que promete "lo que se ve" y entrega otra cosa es la peor clase de error acá:
 * el archivo se ve impecable y nadie descubre el desfase hasta que alguien cuadra los números
 * contra el cliente. Si los alcances difieren, se escriben.
 */
const ALCANCE_ASISTENCIA =
  '«Turnos» trae TODA la programación del rango, incluidos los turnos que nadie marcó y las '
  + 'posiciones sin cubrir. «Marcados» trae solo los marcados que estaban a la vista en pantalla, '
  + 'con los filtros de arriba aplicados. Por eso los dos conteos no tienen por qué cuadrar. '
  + 'Las horas de «Turnos» son la resta salida − ingreso y NO son las horas que se pagan: el '
  + 'cierre de planilla solo acumula cuando el ingreso Y la salida están validados, así que un '
  + 'turno con la salida todavía pendiente aparece acá con sus horas y en la planilla con cero. '
  + 'Los enlaces de foto NO respetan el filtro de arriba: el archivo lleva la foto de todo '
  + 'marcado del rango que salga en cualquiera de las dos hojas, y cada enlace abre sin iniciar '
  + 'sesión.';

/**
 * Las justificaciones aprobadas que la pantalla ya cruzó, listas para el archivo.
 *
 * Las claves son `${workerId}|${date}`, exactamente las mismas que arma `Validacion.tsx`. Se
 * pasan como conjuntos ya resueltos y no como filas de autorizaciones para que este módulo siga
 * siendo puro: acá no se decide qué autorización vale, solo se refleja lo que la pantalla ya
 * pintó en verde.
 *
 * POR QUÉ EXISTE (19-ago): la pantalla mostraba «Falta justificada» y el cierre de planilla le
 * pagaba el día completo, pero el Excel —el único de los tres que sale del sistema por correo—
 * decía «Falta» a secas. Tres respuestas distintas para el mismo día, y la que viajaba al cliente
 * era la que acusaba a la persona.
 */
export interface Justificaciones {
  /** Claves con falta o permiso aprobado: convierten la «Falta» en «Falta justificada». */
  faltas: ReadonlySet<string>;
  /** Claves con tardanza aprobada: la bandera `tardanza` sigue, pero anotada. */
  tardanzas: ReadonlySet<string>;
}

/**
 * La clave `${workerId}|${date}` de un turno, o `null` si no se sabe de quién es.
 *
 * Devuelve `null` cuando el embed de `profiles` vino vacío por RLS (BITACORA §3.7). Ahí no se
 * puede cruzar la autorización y el turno cae en «Falta» sin justificar: es preferible a inventar
 * una clave y justificar la falta de otra persona.
 */
export function claveJustificacion(t: TurnoRow): string | null {
  return t.worker === null ? null : `${t.worker.id}|${t.date}`;
}

/**
 * Inicio y fin del turno como instantes reales, en hora de Lima.
 *
 * `positions_horario` (0001_schema.sql) exige `end_time > start_time`, así que el turno vive
 * siempre dentro de un mismo día y el fin nunca cae antes del inicio.
 */
const inicioDelTurno = (t: TurnoRow): number =>
  new Date(`${t.date}T${t.start_time.slice(0, 5)}:00${LIMA_OFFSET}`).getTime();
const finDelTurno = (t: TurnoRow): number =>
  new Date(`${t.date}T${t.end_time.slice(0, 5)}:00${LIMA_OFFSET}`).getTime();

export type Cumplimiento =
  | 'Ingreso y salida' | 'Sin salida' | 'Sin ingreso' | 'Falta' | 'Falta justificada'
  | 'En curso' | 'Programado' | 'Sin cubrir';

/**
 * Qué pasó con el turno, en una palabra.
 *
 * `Sin cubrir` se decide por la ASIGNACIÓN y no por el nombre del recurso: bajo RLS el embed de
 * `profiles` puede venir null aunque la asignación exista, y ahí el turno está cubierto.
 *
 * **Un turno solo puede ser «Falta» si YA TERMINÓ.** Antes la falta se deducía de que no hubiera
 * marcas, sin mirar el reloj, y el rango por defecto de la pantalla llega hasta hoy con las
 * posiciones del mes ya generadas: un turno de hoy 14:00–22:00 que todavía no empieza salía
 * «Falta» y sumaba a «Faltas» del Resumen; con «Hasta» a fin de mes, TODOS los días futuros
 * salían como falta. La pantalla ya se protege de eso (`listAbsences` corta en `.lt(hoy)`), pero
 * el archivo que se le manda al cliente no lo hacía. De ahí `Programado` (aún no empieza) y
 * `En curso` (empezó y no ha terminado).
 *
 * `En curso` gana sobre `Sin salida`: a media jornada la salida no falta, todavía no toca. Lo
 * mismo con un turno abierto sin ninguna marca — es «En curso», no una falta anticipada.
 *
 * `ahora` entra por parámetro y NO se lee acá con `new Date()`: si no, la función deja de ser
 * pura y no hay forma de probar el día en curso ni el futuro.
 */
export function cumplimientoTurno(
  t: TurnoRow, ahora: Date, faltasJustificadas?: ReadonlySet<string>,
): Cumplimiento {
  if (t.assignmentId === null) return 'Sin cubrir';
  // Con las dos marcas el turno ya se cerró, aunque el reloj todavía no llegue al fin: la persona
  // marcó su salida (temprana o no) y eso es lo que hay que reportar.
  if (t.ingreso !== null && t.salida !== null) return 'Ingreso y salida';
  if (t.salida !== null) return 'Sin ingreso';

  const ms = ahora.getTime();
  const termino = ms >= finDelTurno(t);
  if (t.ingreso !== null) return termino ? 'Sin salida' : 'En curso';
  if (!termino) return ms >= inicioDelTurno(t) ? 'En curso' : 'Programado';

  const clave = claveJustificacion(t);
  return clave !== null && faltasJustificadas !== undefined && faltasJustificadas.has(clave)
    ? 'Falta justificada'
    : 'Falta';
}

/**
 * El rótulo con que cada cumplimiento se cuenta en el `Resumen`, en el orden en que se lee.
 *
 * Es un `Record<Cumplimiento, string>` y no una lista suelta a propósito: así el compilador exige
 * que estén TODOS. Los conteos tienen que sumar exactamente los turnos programados, y una lista
 * que no cierra hace que el primero que la sume desconfíe del archivo entero —con razón, porque
 * lo que falta son turnos que desaparecieron del conteo sin que nadie lo note—. Antes se
 * escribían a mano y un valor nuevo del tipo se podía olvidar acá sin que nada se quejara.
 *
 * El rótulo no es el valor: en la columna del turno se lee «Ingreso y salida» (una fila), en el
 * Resumen «Con ingreso y salida» (un conteo).
 */
const ROTULO_CUMPLIMIENTO: Record<Cumplimiento, string> = {
  // «Con ingreso y salida» y no «Completos»: 'Completo' solo quería decir "hay dos marcas". Un
  // turno 09:00–18:00 con salida a las 14:00 —56 % del horario: bandera `salida_temprana` y medio
  // día en el cierre— salía «Completo», o sea que el archivo daba por cumplido un turno que la
  // planilla paga a la mitad. El nombre ahora dice lo que de verdad se midió.
  'Ingreso y salida': 'Con ingreso y salida',
  'Sin salida': 'Sin salida',
  'Sin ingreso': 'Sin ingreso',
  Falta: 'Faltas',
  'Falta justificada': 'Faltas justificadas',
  'En curso': 'En curso (el turno no ha terminado)',
  Programado: 'Programados (aún no empiezan)',
  'Sin cubrir': 'Sin cubrir',
};

/**
 * Horas efectivamente trabajadas (salida − ingreso), con dos decimales.
 *
 * Falta cualquiera de las dos marcas → **0**, y el 0 se escribe: es el pedido textual del usuario
 * ("turnos que no se cumplieron y están en 0 deberían aparecer"). Una celda vacía se lee como
 * "falta el dato" y alguien va a ir a buscarlo; el 0 se lee como "no trabajó", que es lo que pasó.
 *
 * Una diferencia negativa o absurda también da 0. Puede ocurrir con una salida fijada a mano con
 * la hora mal tecleada, y un número negativo en la columna de horas se arrastraría a la suma del
 * `Resumen` restando horas de otras personas.
 *
 * Se resta sobre los `device_ts` completos, así que una salida marcada después de la medianoche
 * —turno 16:00–23:00 con la salida real a las 00:20— sale bien sin tratarla aparte: las dos
 * marcas ya traen su día. (El turno en sí nunca cruza la medianoche: `positions_horario` exige
 * `end_time > start_time`; lo que sí puede cruzarla es el marcado.)
 *
 * **Estas NO son las horas que se pagan.** Acá no se mira `status`: una salida `pendiente` u
 * `observada` igual suma. `computePayroll` solo acumula cuando el ingreso Y la salida están
 * `validada`, así que el mismo turno puede dar 9.1 h en este archivo y 0 en la planilla. El
 * `Resumen` lo dice en el rótulo de la fila y en el `Alcance`, porque el número se copia a mano
 * a otros lados.
 */
export function horasTrabajadas(t: TurnoRow): number {
  if (t.ingreso === null || t.salida === null) return 0;
  const ms = new Date(t.salida.device_ts).getTime() - new Date(t.ingreso.device_ts).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.round(ms / 36_000) / 100;
}

/**
 * La unión de las banderas de las dos marcas, sin repetir y ya traducidas.
 *
 * Sin deduplicar, `Sin GPS` (que se marca en ingreso y en salida por separado) saldría dos veces
 * en la misma celda y parecería un error del reporte.
 *
 * `tardanzaJustificada` NO borra la bandera `Tardanza`: agrega «Tardanza justificada» al lado,
 * igual que la pantalla, que pinta las dos insignias. Borrarla escondería el hecho (llegó tarde)
 * y dejaría el archivo sin explicación de por qué esa tardanza no se descuenta; dejarla sola
 * convierte una tardanza autorizada por el jefe en una acusación.
 */
export function banderasTurno(t: TurnoRow, tardanzaJustificada = false): string {
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const m of [t.ingreso, t.salida]) {
    for (const f of m?.flags ?? []) {
      if (vistas.has(f)) continue;
      vistas.add(f);
      out.push(FLAG_LABEL[f] ?? f);
    }
  }
  if (tardanzaJustificada && vistas.has('tardanza')) out.push('Tardanza justificada');
  return out.join(', ');
}

/**
 * El estado de validación del turno. Si las dos marcas coinciden es una sola palabra; si difieren
 * se dicen las dos, porque validar el ingreso y observar la salida es un caso real (la salida
 * fijada a mano por el coordinador) y aplanarlo a un solo estado escondería la observación.
 */
export function estadoTurno(t: TurnoRow): string {
  const texto = (m: TurnoMarca | null): string | null =>
    m === null ? null : (ESTADO_ASISTENCIA[m.status] ?? m.status);
  const i = texto(t.ingreso);
  const s = texto(t.salida);
  if (i === null && s === null) return '';
  if (i === null) return s as string;
  if (s === null) return i;
  return i === s ? i : `Ingreso: ${i} · Salida: ${s}`;
}

/**
 * Las banderas de UN marcado suelto (hoja `Marcados`), con la tardanza anotada si estaba
 * autorizada. Mismo criterio que `banderasTurno`: la bandera no se borra, se explica.
 */
export function banderasMarca(r: ValidationRow, tardanzasJustificadas: ReadonlySet<string>): string {
  const out = r.flags.map((f) => FLAG_LABEL[f] ?? f);
  const clave = `${r.assignment.worker.id}|${r.assignment.position.date}`;
  if (r.flags.includes('tardanza') && tardanzasJustificadas.has(clave)) {
    out.push('Tardanza justificada');
  }
  return out.join(', ');
}

/** Las notas de validación que existan, unidas. Las vacías no ensucian la celda con separadores. */
export function notaTurno(t: TurnoRow): string {
  return [t.ingreso?.validation_note, t.salida?.validation_note]
    .filter((n): n is string => typeof n === 'string' && n.trim() !== '')
    .join(' · ');
}

const nombreRecursoTurno = (t: TurnoRow): string =>
  t.worker === null ? '' : `${t.worker.first_names} ${t.worker.last_names}`;

const gpsMarca = (m: TurnoMarca | null): string =>
  m === null || m.lat === null || m.lng === null ? '' : `${m.lat}, ${m.lng}`;

/** `HH:MM` en hora de Lima, o vacío si esa marca no existe. */
const horaMarca = (m: TurnoMarca | null): string =>
  m === null ? '' : formatInTimeZone(m.device_ts, TZ, 'HH:mm');

/**
 * Orden del archivo: fecha, tienda, recurso. Se usa como plantilla de programación y se lee de
 * arriba abajo; ordenado por cualquier otra cosa, quien revisa una tienda tiene que ir saltando
 * por toda la hoja. La `key` desempata al final para que dos descargas seguidas del mismo rango
 * salgan idénticas — si no, comparar dos versiones del archivo es imposible.
 *
 * Ordena una COPIA a propósito: el arreglo que llega es estado de un componente de React y
 * reordenarlo en el sitio movería la lista bajo los pies del usuario mientras descarga.
 */
export function ordenarTurnos(turnos: TurnoRow[]): TurnoRow[] {
  return [...turnos].sort((a, b) =>
    a.date.localeCompare(b.date)
    || (a.pdv?.name ?? '').localeCompare(b.pdv?.name ?? '', 'es')
    || nombreRecursoTurno(a).localeCompare(nombreRecursoTurno(b), 'es')
    || a.key.localeCompare(b.key));
}

/**
 * Reporte de asistencia. La hoja principal es `Turnos`: UNA fila por turno programado, con el
 * ingreso y la salida en columnas distintas.
 *
 * Antes había una sola hoja con una fila por MARCADO, y aquí decía que no se emparejaban a
 * propósito: que emparejar dejaría el Excel con menos filas que la pantalla y alguien creería que
 * el reporte se comió marcados. El emparejamiento nunca fue el problema —`attendance` tiene
 * UNIQUE(assignment_id, kind), o sea que por asignación hay como mucho un ingreso y una salida, y
 * juntarlos no es ambiguo jamás—. El miedo se resuelve de otro modo: la hoja `Marcados` se
 * conserva detrás con cada marca suelta (banderas, GPS y foto propias) y el `Resumen` dice los DOS
 * conteos, así que nadie tiene que adivinar por qué un número no es el otro.
 *
 * `Turnos` trae la programación entera del rango, cumplida o no, incluidas las posiciones que
 * nadie cubrió: el archivo se usa como plantilla de programación lista para llenarse con las
 * asistencias, y un turno sin marcar tiene que quedar en su línea con 0 horas a la vista.
 */
export function buildAsistenciaWorkbook(
  meta: ReporteMeta, turnos: TurnoRow[], rows: ValidationRow[], enlaces: EnlacesDeFoto,
  justificaciones: Justificaciones,
  generatedAt: Date = new Date(), vigenciaSegundos: number = ENLACE_REPORTE_SEGUNDOS,
): ArrayBuffer {
  // El "ahora" con el que se decide si un turno ya terminó es el MISMO instante que el archivo
  // estampa en «Generado el». No es un atajo: con dos relojes distintos el archivo podría decir
  // "generado el 18/08 10:30" y a la vez marcar como «Falta» un turno que termina a las 11:00 de
  // ese mismo día, y quien lo lea no tendría cómo detectar la contradicción.
  const ahora = generatedAt;
  const tardanzaJustificada = (t: TurnoRow): boolean => {
    const k = claveJustificacion(t);
    return k !== null && justificaciones.tardanzas.has(k);
  };

  const ordenados = ordenarTurnos(turnos);
  const cuerpoTurnos = ordenados.map((t) => [
    t.date,
    t.pdv?.name ?? '',
    nombreRecursoTurno(t),
    t.worker?.dni ?? '',
    `${t.start_time.slice(0, 5)}-${t.end_time.slice(0, 5)}`,
    horaMarca(t.ingreso),
    horaMarca(t.salida),
    horasTrabajadas(t),
    cumplimientoTurno(t, ahora, justificaciones.faltas),
    banderasTurno(t, tardanzaJustificada(t)),
    estadoTurno(t),
    notaTurno(t),
    gpsMarca(t.ingreso),
    gpsMarca(t.salida),
    '', '', // las dos columnas de foto se reemplazan abajo por celdas con hipervínculo
  ]);

  const wsTurnos = XLSX.utils.aoa_to_sheet([TURNOS_HEADERS, ...cuerpoTurnos]);
  ordenados.forEach((t, i) => {
    // Mismo motivo que en la hoja de marcados: como número, Excel se come los ceros a la
    // izquierda del documento y el cruce por documento deja de calzar.
    wsTurnos[`D${i + 2}`] = { t: 's', v: t.worker?.dni ?? '' };
    wsTurnos[`O${i + 2}`] = celdaEnlace('Ver foto', photoLink(enlaces, t.ingreso?.photo_path ?? null));
    wsTurnos[`P${i + 2}`] = celdaEnlace('Ver foto', photoLink(enlaces, t.salida?.photo_path ?? null));
  });
  wsTurnos['!cols'] = [
    { wch: 12 }, { wch: 26 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 8 },
    { wch: 8 }, { wch: 13 }, { wch: 26 }, { wch: 26 }, { wch: 40 }, { wch: 24 }, { wch: 24 },
    { wch: 12 }, { wch: 12 },
  ];

  // Hoja `Marcados`: el detalle, una fila por marca suelta. Es lo que hace seguro emparejar en
  // `Turnos` — las banderas, el GPS y la foto de cada marca individual siguen estando, así que
  // nada de lo que se veía antes se perdió al juntar el turno en una línea.
  const cuerpo = rows.map((r) => [
    r.assignment.position.date,
    r.assignment.position.pdv?.name ?? '',
    `${r.assignment.worker.first_names} ${r.assignment.worker.last_names}`,
    r.assignment.worker.dni,
    r.kind === 'ingreso' ? 'Ingreso' : 'Salida',
    formatInTimeZone(r.device_ts, TZ, 'HH:mm'),
    `${r.assignment.position.start_time.slice(0, 5)}-${r.assignment.position.end_time.slice(0, 5)}`,
    // La justificación se anota también acá y no solo en `Turnos`: es la MISMA tardanza vista de
    // otro modo, y si una hoja la explica y la otra no, la que se lea primero decide.
    banderasMarca(r, justificaciones.tardanzas),
    ESTADO_ASISTENCIA[r.status] ?? r.status,
    r.validation_note ?? '',
    r.lat === null || r.lng === null ? '' : `${r.lat}, ${r.lng}`,
    '', // la columna de foto se reemplaza abajo por una celda con hipervínculo
  ]);

  const ws = XLSX.utils.aoa_to_sheet([ASISTENCIA_HEADERS, ...cuerpo]);
  rows.forEach((r, i) => {
    // El documento se fuerza a texto: como número, Excel se come los ceros a la izquierda y
    // el cruce por documento —que en este sistema es el único válido— deja de calzar.
    ws[`D${i + 2}`] = { t: 's', v: r.assignment.worker.dni };
    ws[`L${i + 2}`] = celdaEnlace('Ver foto', photoLink(enlaces, r.photo_path));
  });
  ws['!cols'] = [
    { wch: 12 }, { wch: 26 }, { wch: 30 }, { wch: 12 }, { wch: 9 }, { wch: 8 },
    { wch: 12 }, { wch: 26 }, { wch: 11 }, { wch: 40 }, { wch: 24 }, { wch: 10 },
  ];

  const cumplimientos = ordenados.map((t) => cumplimientoTurno(t, ahora, justificaciones.faltas));
  const cuentaTurnos = (c: Cumplimiento): number =>
    cumplimientos.filter((x) => x === c).length;
  // `horasTrabajadas` ya devuelve CADA turno redondeado a dos decimales, así que este `Math.round`
  // no gana precisión: limpia el arrastre binario de sumar cien valores como 9.1 —que en coma
  // flotante da 909.9999999999999— y que el total del mes salga con catorce decimales en la celda.
  const horasTotal =
    Math.round(ordenados.reduce((s, t) => s + horasTrabajadas(t), 0) * 100) / 100;

  const conAlerta = rows.filter((r) => r.flags.length > 0).length;
  const resumen = hojaResumen(meta, 'Reporte de asistencia', [
    ['Turnos programados', ordenados.length],
    // El orden es el del literal de `ROTULO_CUMPLIMIENTO`, que es el orden en que se leen.
    ...(Object.keys(ROTULO_CUMPLIMIENTO) as Cumplimiento[])
      .map((c) => [ROTULO_CUMPLIMIENTO[c], cuentaTurnos(c)] as [string, number]),
    // El rótulo lleva la advertencia encima porque este número se copia a mano a otros lados y
    // ahí ya nadie tiene el `Alcance` a la vista. Ver `horasTrabajadas`.
    ['Horas marcadas (no es lo que se paga)', horasTotal],
    ['Marcados', rows.length],
    ['Ingresos', rows.filter((r) => r.kind === 'ingreso').length],
    ['Salidas', rows.filter((r) => r.kind === 'salida').length],
    ['Con alguna alerta', conAlerta],
    ['Pendientes de validar', rows.filter((r) => r.status === 'pendiente').length],
    ['Validados', rows.filter((r) => r.status === 'validada').length],
    ['Observados', rows.filter((r) => r.status === 'observada').length],
  ], generatedAt, vigenciaSegundos, ALCANCE_ASISTENCIA);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, resumen, 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsTurnos, 'Turnos');
  // La hoja se llama `Marcados` y no `Asistencias`: ahora hay dos hojas de asistencia y el nombre
  // viejo no distinguía cuál era cuál. Va DESPUÉS de `Turnos` porque es el detalle, no la portada.
  XLSX.utils.book_append_sheet(wb, ws, 'Marcados');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

// ---------------------------------------------------------------------------
// Canjes
// ---------------------------------------------------------------------------

export const CANJES_HEADERS = [
  'Fecha', 'Tienda', 'Recurso', 'Documento', 'Canjes', 'Dinámicas', 'Premios',
  'Cliente', 'Contacto', 'Monto S/', 'N.º boleta', 'Alertas', 'Estado',
  'Registrado el', 'Observaciones', 'Foto boleta', 'Foto premio', 'Cargado a mano',
];

export const CANJES_LINEAS_HEADERS = [
  'Fecha', 'Tienda', 'Recurso', 'Dinámica', 'Canjes', 'Premio', 'Unidades', 'Equivalencia',
];

/**
 * Lo que dice la hoja `Resumen` sobre qué está contando.
 *
 * Va escrito porque el archivo lista los anulados en la hoja `Canjes` (con su estado a la vista,
 * que es la evidencia de que se anularon) pero NO los suma en ningún conteo. Sin esta línea, quien
 * cuente las filas de la hoja obtiene un número distinto del que dice el Resumen y no tiene forma
 * de saber cuál es el bueno — que es exactamente el error que hizo falta arreglar (ver
 * `domain/canjesTotales.ts`).
 */
const ALCANCE_CANJES =
  'Todas las cifras de este resumen cuentan solo los registros VIGENTES. Los anulados siguen en '
  + 'la hoja «Canjes», marcados como «Anulado», y se informan aparte en «Anulados»: anular es la '
  + 'forma de eliminar acá, así que el canje queda a la vista pero no suma en nada.';

const nombreRecurso = (r: CanjeRow): string =>
  r.worker === null ? '' : `${r.worker.firstNames} ${r.worker.lastNames}`;

/**
 * La columna «Cargado a mano» (0374). Va al final y NO en «Alertas»: las alertas salen de
 * `banderasDeCanje`, que comparte el HTML que se le manda al cliente, y el cliente no tiene por qué
 * leer cómo se cargó cada canje. El Excel es interno y tiene que poder separarlo.
 */
const cargaAMano = (r: CanjeRow): string => {
  if (r.origen !== 'bandeja') return '';
  const quien = r.cargadoPor ? ` por ${r.cargadoPor.firstNames} ${r.cargadoPor.lastNames}` : '';
  return `Sí${quien}${r.motivoCarga ? `: ${r.motivoCarga}` : ''}`;
};

/**
 * Reporte de canjes: hoja `Canjes` con una fila por registro y hoja `Líneas` con el desglose
 * por dinámica y premio.
 *
 * Las dos hojas hacen falta. El registro es la unidad que se corrige y se anula; la línea es
 * la unidad con la que el cliente cuenta los premios entregados. Con una sola hoja, o se
 * pierde el desglose o se repiten los datos del cliente en cada línea y los montos se suman
 * dos veces.
 */
export function buildCanjesWorkbook(
  meta: ReporteMeta, rows: CanjeRow[], enlaces: EnlacesDeFoto,
  generatedAt: Date = new Date(), vigenciaSegundos: number = ENLACE_REPORTE_SEGUNDOS,
): ArrayBuffer {
  const cuerpo = rows.map((r) => [
    r.fecha,
    r.pdv?.name ?? '',
    nombreRecurso(r),
    r.worker?.dni ?? '',
    r.totalCanjes,
    r.lineas.map((l) => l.dynamicName).join(', '),
    r.lineas.flatMap((l) => l.premios.map((p) => `${p.prizeName} x${p.units}`)).join(', '),
    r.clienteNombre,
    r.clienteContacto ?? '',
    r.montoCompra ?? '',
    r.boletaNumero ?? '',
    banderasDeCanje(r).join(', '),
    r.estado === 'anulado' ? 'Anulado' : 'Registrado',
    formatInTimeZone(r.registradoEn, TZ, 'dd/MM/yyyy HH:mm'),
    r.observaciones ?? '',
    '', '',
    cargaAMano(r),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([CANJES_HEADERS, ...cuerpo]);
  rows.forEach((r, i) => {
    ws[`D${i + 2}`] = { t: 's', v: r.worker?.dni ?? '' };
    // El número de boleta también es texto: hay series como "BA26-03761398" y otras que son
    // solo dígitos; si Excel decide por fila, la misma columna sale mitad número mitad texto.
    ws[`K${i + 2}`] = { t: 's', v: r.boletaNumero ?? '' };
    ws[`P${i + 2}`] = celdaEnlace('Ver boleta', photoLink(enlaces, r.fotoBoletaPath));
    ws[`Q${i + 2}`] = celdaEnlace('Ver premio', photoLink(enlaces, r.fotoPremioPath));
  });
  ws['!cols'] = [
    { wch: 12 }, { wch: 26 }, { wch: 30 }, { wch: 12 }, { wch: 8 }, { wch: 24 }, { wch: 30 },
    { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 18 }, { wch: 30 }, { wch: 11 },
    { wch: 18 }, { wch: 34 }, { wch: 12 }, { wch: 12 }, { wch: 40 },
  ];

  const lineas = rows.flatMap((r) =>
    r.lineas.flatMap((l) => {
      const cabecera = [r.fecha, r.pdv?.name ?? '', nombreRecurso(r),
        l.dynamicName, l.canjes];
      return l.premios.length === 0
        ? [[...cabecera, '', '', '']]
        : l.premios.map((p) => [...cabecera, p.prizeName, p.units, p.equivalencia ? 'Sí' : 'No']);
    }),
  );
  const wsLineas = XLSX.utils.aoa_to_sheet([CANJES_LINEAS_HEADERS, ...lineas]);
  wsLineas['!cols'] = [
    { wch: 12 }, { wch: 26 }, { wch: 30 }, { wch: 24 }, { wch: 8 },
    { wch: 24 }, { wch: 10 }, { wch: 14 },
  ];

  // Las cifras salen de `domain/canjesTotales.ts` y NO se recalculan acá. Antes esta hoja sumaba
  // `rows.length` y `totalCanjes` de TODAS las filas, anuladas incluidas, mientras el HTML que
  // sale del mismo botón ya las excluía: los dos archivos se le mandan al mismo cliente con dos
  // números distintos de canjes y ninguno de los dos dice cuál es el bueno.
  const totales = totalesGenerales(rows);
  const vigentes = registrosVigentes(rows);
  const resumen = hojaResumen(meta, 'Reporte de canjes', [
    // «Registros vigentes» y no «Registros»: el rótulo tiene que decir que los anulados no están,
    // porque la hoja `Canjes` sí los lista y las dos cifras se comparan a ojo.
    ['Registros vigentes', totales.registros],
    ['Canjes (suma de líneas)', totales.canjes],
    ['Anulados', totales.anulados],
    // Las banderas también se cuentan sobre los vigentes: un registro anulado por estar fuera de
    // programación inflaría «Fuera de programación» con el problema que ya se corrigió anulándolo.
    ['Fuera de programación', vigentes.filter((r) => r.fueraDeProgramacion).length],
    ['Registro tardío', vigentes.filter((r) => r.registroTardio).length],
    ['Sin asistencia', vigentes.filter((r) => r.sinAsistencia).length],
    ['Con foto de boleta', vigentes.filter((r) => r.fotoBoletaPath !== null).length],
    ['Con número de boleta', vigentes.filter((r) => (r.boletaNumero ?? '') !== '').length],
  ], generatedAt, vigenciaSegundos, ALCANCE_CANJES);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, resumen, 'Resumen');
  XLSX.utils.book_append_sheet(wb, ws, 'Canjes');
  XLSX.utils.book_append_sheet(wb, wsLineas, 'Líneas');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

export function reporteFileName(tipo: 'asistencia' | 'canjes', meta: ReporteMeta): string {
  return `reporte-${tipo}-${slugify(meta.campaignName)}-`
    + `${meta.periodStart}-a-${meta.periodEnd}.xlsx`;
}
